import type { ReactNode } from 'react';
import type { ValidationIssue } from '../types';

interface SectionProps {
  index: string;
  title: string;
  purpose: string;
  children: ReactNode;
  id: string;
}

/**
 * Sections are numbered because the order is real: the tool is designed to be
 * read as a sequence (calculate, explain, explore, consider, verify, document).
 * The numbering is information, not decoration.
 */
export function Section({ index, title, purpose, children, id }: SectionProps) {
  return (
    <section className="section" id={id} aria-labelledby={`${id}-title`}>
      <header className="section-head">
        <span className="section-index" aria-hidden="true">
          {index}
        </span>
        <div>
          <h2 className="section-title" id={`${id}-title`}>
            {title}
          </h2>
          <p className="section-purpose">{purpose}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

/**
 * The signature device of this interface.
 *
 * Any value that is an *assumption* rather than a measurement or a derived
 * result is marked with this tag, in the same ochre, everywhere it appears —
 * input field, result panel, derivation, sensitivity table, summary. The point
 * of the tool is that assumptions stay visible, so they are given a consistent
 * visual identity instead of blending into the surrounding numbers.
 */
export function AssumptionTag({ children }: { children: ReactNode }) {
  return (
    <span className="assumption">
      <span className="assumption-value">{children}</span>
      <span className="assumption-label">assumption</span>
    </span>
  );
}

export function Panel({
  title,
  children,
  tone = 'default',
}: {
  title?: string;
  children: ReactNode;
  tone?: 'default' | 'quiet' | 'flagged';
}) {
  return (
    <div className={`panel panel-${tone}`}>
      {title ? <h3 className="panel-title">{title}</h3> : null}
      {children}
    </div>
  );
}

export function ValidationMessages({ issues }: { issues: ValidationIssue[] }) {
  if (issues.length === 0) return null;
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');

  return (
    <div className="validation" role="status" aria-live="polite">
      {errors.length > 0 && (
        <div className="validation-group validation-error">
          <p className="validation-kind">
            Cannot calculate <span className="validation-kind-sub">the arithmetic is undefined for these inputs</span>
          </p>
          <ul>
            {errors.map((issue) => (
              <li key={issue.id}>
                <strong>{issue.message}</strong>
                <span>{issue.explanation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="validation-group validation-warning">
          <p className="validation-kind">
            Worth a look{' '}
            <span className="validation-kind-sub">
              the arithmetic is valid — whether the input is right for your system is your call
            </span>
          </p>
          <ul>
            {warnings.map((issue) => (
              <li key={issue.id}>
                <strong>{issue.message}</strong>
                <span>{issue.explanation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function Disclosure({
  summary,
  children,
  onOpen,
  defaultOpen = false,
}: {
  summary: string;
  children: ReactNode;
  onOpen?: () => void;
  defaultOpen?: boolean;
}) {
  return (
    <details
      className="disclosure"
      open={defaultOpen}
      onToggle={(event) => {
        if ((event.currentTarget as HTMLDetailsElement).open) onOpen?.();
      }}
    >
      <summary>{summary}</summary>
      <div className="disclosure-body">{children}</div>
    </details>
  );
}
